// iter_CONFIGURABLE_PUBLIC_VENUE_LANDING_PAGE (2026-02-28)
// Public venue contact page. Dynamically renders the venue's public
// contact details, business hours, and delivery info from the same
// `/api/public/venues/{venue_id}/landing-page` payload used by the
// landing page. Never invents URLs; every action button uses the
// standard tel: / mailto: / venue-supplied url schemes.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { ContactSummary, HoursSummary, DeliverySummary } from "./PublicVenueLanding";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicVenueContact() {
  const { venueId } = useParams();
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/venues/${venueId}/landing-page`);
        if (!alive) return;
        if (r.data?.enabled === false) {
          setState("disabled"); return;
        }
        setState("ready"); setData(r.data);
      } catch (e) {
        if (!alive) return;
        const s = e?.response?.status;
        setState(s === 404 ? "not_found" : "error");
        setErrorMsg(e?.response?.data?.detail || "Unable to load venue.");
      }
    })();
    return () => { alive = false; };
  }, [venueId]);

  if (state === "loading") {
    return <div className="p-8 text-center text-stone-500" data-testid="vlp-contact-loading">Loading…</div>;
  }
  if (state === "not_found") {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" data-testid="vlp-contact-not-found">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-semibold">Venue not found</h1>
        </div>
      </div>
    );
  }
  if (state === "error") {
    return <div className="p-8 text-center text-red-600" data-testid="vlp-contact-error">{errorMsg}</div>;
  }
  if (state === "disabled") {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" data-testid="vlp-contact-disabled">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-semibold">This venue's page is not available</h1>
        </div>
      </div>
    );
  }

  const v = data.venue;
  const c = v.contact || {};

  return (
    <div className="min-h-screen bg-stone-50" data-testid="vlp-contact-page">
      <div className="max-w-2xl mx-auto p-4 sm:p-8">
        <header>
          <h1 className="text-2xl sm:text-3xl font-semibold text-stone-900" data-testid="vlp-contact-name">
            {v.display_name}
          </h1>
          {v.subtitle && <p className="mt-1 text-stone-600">{v.subtitle}</p>}
        </header>

        <section className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {c.phone && (
            <ActionButton href={`tel:${c.phone}`} label="Call" testid="vlp-btn-call" />
          )}
          {c.email && (
            <ActionButton href={`mailto:${c.email}`} label="Email" testid="vlp-btn-email" />
          )}
          {c.website && (
            <ActionButton href={c.website} label="Website" testid="vlp-btn-website" external />
          )}
          {c.maps_url && (
            <ActionButton href={c.maps_url} label="Get Directions" testid="vlp-btn-directions" external />
          )}
        </section>

        <ContactSummary contact={c} />

        <HoursSummary hours={v.business_hours} />

        <DeliverySummary delivery={v.delivery} />

        <Link
          to={`/venue/${v.venue_id}`}
          className="mt-8 inline-block text-sm underline text-stone-600 hover:text-stone-900"
          data-testid="vlp-back-home"
        >
          ← Back to venue home
        </Link>

        {v.footer_note && (
          <footer className="mt-10 pt-6 border-t border-stone-200 text-xs text-stone-500">
            {v.footer_note}
          </footer>
        )}
      </div>
    </div>
  );
}


function ActionButton({ href, label, testid, external }) {
  const props = external ? { target: "_blank", rel: "noreferrer" } : {};
  return (
    <a
      href={href}
      className="inline-flex items-center justify-center rounded-md bg-stone-900 px-4 py-3 text-sm font-medium text-white hover:bg-stone-800 min-h-[44px]"
      data-testid={testid}
      {...props}
    >
      {label}
    </a>
  );
}
